import React, { useEffect } from "react";
import { connect } from "react-redux";
import BVASelect from "../../../../components/BVASelect/BVASelect";
import {
  fetchCompanyMonthlyProfitAndLoss,
  updateMonthlyProfitAndLoss,
} from "../../../../store/modules/financials/actions";
import { updateCategory } from "../../../../store/modules/category/actions";
import { Divider } from "@mui/material";
import ProfitAndLossRatiosConsolidation from "./ProfitAndLossConsolidation/ProfitAndLossConsolidation";
import ProfitAndLoss from "../../../../models/ProfitAndLoss";
import FinancialHandler from "../../../../handlers/FinancialHandler";
import InlineLoading from "../../../../components/Loading/Loading";
import MonthlyProfitAndLoss from "../../../../models/financials/MonthlyProfitAndLoss";
import { primary } from "../../../../assets/colorPallet";
import ProfitAndLossEbitda from "./ProfitAndLossEbitda/ProfitAndLossEbitda";

function MonthlyProfitAndLossDashboard({
  companyData,
  dataSettings,
  dateOrder,
  currentCompanyMonthlyProfitAndLoss,
  dispatchFetchMonthlyProfitAndLoss,
  dispatchUpdateMonthlyProfitAndLoss,
  dispatchUpdateCategory
}) {
  // Mounted
  useEffect(() => {
    handleFetchMonthlyProfitAndLoss();

    ////Initialize categories using GPT
    currentCompanyMonthlyProfitAndLoss?.getNames("incomes")?.map((name) => 
      !currentCompanyMonthlyProfitAndLoss?.getCategory({ name, type: "incomes" })
      && handleOnClickGPT({ name, type: "incomes" })
    )   //execute handleOnClickGPT if the category is undefined
    currentCompanyMonthlyProfitAndLoss?.getNames("costOfSales")?.map((name) => 
      !currentCompanyMonthlyProfitAndLoss?.getCategory({ name, type: "costOfSales" })
      && handleOnClickGPT({ name, type: "costOfSales" })
    )
    currentCompanyMonthlyProfitAndLoss?.getNames("expenses")?.map((name) => 
      !currentCompanyMonthlyProfitAndLoss?.getCategory({ name, type: "expenses" })
      && handleOnClickGPT({ name, type: "expenses" })
    )
    currentCompanyMonthlyProfitAndLoss?.getNames("otherIncomes")?.map((name) => 
      !currentCompanyMonthlyProfitAndLoss?.getCategory({ name, type: "otherIncomes" })
      && handleOnClickGPT({ name, type: "otherIncomes" })
    )
    currentCompanyMonthlyProfitAndLoss?.getNames("otherExpenses")?.map((name) => 
      !currentCompanyMonthlyProfitAndLoss?.getCategory({ name, type: "otherExpenses" })
      && handleOnClickGPT({ name, type: "otherExpenses" })
    )
  }, []);

  // Methods
  const handleFetchMonthlyProfitAndLoss = async () => {
    await dispatchFetchMonthlyProfitAndLoss(
      { companyId: companyData.companyId },
      { periodsToCompare: dataSettings.yearsToFetch },
      { dateOrder }
    );
  };

  const handleCategoryOnChange = ({ name, type }, { category }) => {
    dispatchUpdateCategory(name, category);
    dispatchUpdateMonthlyProfitAndLoss({ name, type }, { category });
  };

  const handleOnClickGPT = async ({ name, type }) => {
    const category = await FinancialHandler.fetchGPTCategorySelection(name, type);
    handleCategoryOnChange({ name, type }, { category })
  }

  if (!(currentCompanyMonthlyProfitAndLoss instanceof MonthlyProfitAndLoss)) {
    return <InlineLoading />;
  }

  return (
    <div>
      <div className="overflow-auto">
        <div className="flex items-center w-full">
          <div
            style={{ backgroundColor: primary }}
            className="absolute left-0 w-full h-12 bg-gradient-to-r from-transparent z-10 pointer-events-none"
          ></div>
          <div className="z-20" style={{ minWidth: "36em" }}></div>
          <div className="z-20" style={{ minWidth: "14em" }}>
            <label className="text-white font-semibold">Category</label>
          </div>
          {currentCompanyMonthlyProfitAndLoss?.datesFormatted?.map((date) => (
            <div
              className="text-white z-20 w-40 font-semibold"
              style={{ minWidth: "10em" }}
            >
              <label>{date}</label>
            </div>
          ))}
        </div>

        <div className="mt-10">
          <div className="mb-5">
            <div className="flex items-center">
              <div className="absolute left-0 w-full h-8 bg-gradient-to-r from-transparent bg-neutral-100 z-10 pointer-events-none"></div>
              <label className="z-20 font-semibold">Income</label>
            </div>
            <div className="mt-5">
              {currentCompanyMonthlyProfitAndLoss?.getNames("incomes")?.map((name, incomeIndex) => (
                <div key={incomeIndex} className="flex items-center mb-2">
                  <div className="whitespace-pre-wrap flex items-center" style={{ minWidth: "27em" }}>
                    <label>{name.split(":").length === 3 ? [name.split(":")[0], name.split(":")[2]].join(":") : name}</label>
                  </div>
                  <button style={{ minWidth: "3em" }} onClick={() => handleOnClickGPT({ name, type: "incomes" })}>🤖</button>
                  <div className="flex items-center" style={{ minWidth: "20em" }}>
                    <BVASelect
                      items={ProfitAndLoss.getCategoriesOptions("incomes")}
                      itemLabel="name"
                      width="14em"
                      placeholder="Select a category"
                      value={currentCompanyMonthlyProfitAndLoss?.getCategory({ name, type: "incomes" })}
                      onChange={(category) => handleCategoryOnChange({ name, type: "incomes" }, { category })}
                    />
                  </div>
                  {currentCompanyMonthlyProfitAndLoss?.getItems("incomes", name)?.map((fixedAsset) => (
                    <div className="flex items-center" style={{ minWidth: "10em" }}>
                      <label>{fixedAsset?.formattedValue}</label>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="mb-5">
            <div className="flex items-center">
              <div className="absolute left-0 w-full h-8 bg-gradient-to-r from-transparent bg-neutral-100 z-10 pointer-events-none"></div>
              <label className="z-20 font-semibold">Cost of Sales</label>
            </div>
            <div className="mt-5">
              {currentCompanyMonthlyProfitAndLoss?.getNames("costOfSales")?.map((name, costIndex) => (
                <div key={costIndex} className="flex items-center mb-2">
                  <div className="whitespace-pre-wrap flex items-center" style={{ minWidth: "27em" }}>
                    <label>{name.split(":").length === 3 ? [name.split(":")[0], name.split(":")[2]].join(":") : name}</label>
                  </div>
                  <button style={{ minWidth: "3em" }} onClick={() => handleOnClickGPT({ name, type: "costOfSales" })}>🤖</button>
                  <div className="flex items-center" style={{ minWidth: "20em" }}>
                    <BVASelect
                      items={ProfitAndLoss.getCategoriesOptions("costOfSales")}
                      itemLabel="name"
                      width="14em"
                      placeholder="Select a category"
                      value={currentCompanyMonthlyProfitAndLoss?.getCategory({ name, type: "costOfSales" })}
                      onChange={(category) => handleCategoryOnChange({ name, type: "costOfSales" }, { category })}
                    />
                  </div>
                  {currentCompanyMonthlyProfitAndLoss?.getItems("costOfSales", name)?.map((fixedAsset) => (
                    <div className="flex items-center" style={{ minWidth: "10em" }}>
                      <label>{fixedAsset?.formattedValue}</label>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="mb-5">
            <div className="flex items-center">
              <div className="absolute left-0 w-full h-8 bg-gradient-to-r from-transparent bg-neutral-100 z-10 pointer-events-none"></div>
              <label className="z-20 font-semibold">Expenses</label>
            </div>
            <div className="mt-5">
              {currentCompanyMonthlyProfitAndLoss?.getNames("expenses")?.map((name, incomeIndex) => (
                <div key={incomeIndex} className="flex items-center mb-2">
                  <div className="whitespace-pre-wrap flex items-center" style={{ minWidth: "27em" }}>
                    <label>{name.split(":").length === 3 ? [name.split(":")[0], name.split(":")[2]].join(":") : name}</label>
                  </div>
                  <button style={{ minWidth: "3em" }} onClick={() => handleOnClickGPT({ name, type: "expenses" })}>🤖</button>
                  <div className="flex items-center" style={{ minWidth: "20em" }}>
                    <BVASelect
                      items={ProfitAndLoss.getCategoriesOptions("expenses")}
                      itemLabel="name"
                      width="14em"
                      placeholder="Select a category"
                      value={currentCompanyMonthlyProfitAndLoss?.getCategory({ name, type: "expenses" })}
                      onChange={(category) => handleCategoryOnChange({ name, type: "expenses" }, { category })}
                    />
                  </div>
                  {currentCompanyMonthlyProfitAndLoss?.getItems("expenses", name)?.map((fixedAsset) => (
                    <div className="flex items-center" style={{ minWidth: "10em" }}>
                      <label>{fixedAsset?.formattedValue}</label>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="mb-5">
            <div className="flex items-center">
              <div className="absolute left-0 w-full h-8 bg-gradient-to-r from-transparent bg-neutral-100 z-10 pointer-events-none"></div>
              <label className="z-20 font-semibold">Other Incomes</label>
            </div>
            <div className="mt-5">
              {currentCompanyMonthlyProfitAndLoss?.getNames("otherIncomes")?.map((name, incomeIndex) => (
                <div key={incomeIndex} className="flex items-center mb-2">
                  <div className="whitespace-pre-wrap flex items-center" style={{ minWidth: "27em" }}>
                    <label>{name.split(":").length === 3 ? [name.split(":")[0], name.split(":")[2]].join(":") : name}</label>
                  </div>
                  <button style={{ minWidth: "3em" }} onClick={() => handleOnClickGPT({ name, type: "otherIncomes" })}>🤖</button>
                  <div className="flex items-center" style={{ minWidth: "20em" }}>
                    <BVASelect
                      items={ProfitAndLoss.getCategoriesOptions("otherIncomes")}
                      itemLabel="name"
                      width="14em"
                      placeholder="Select a category"
                      value={currentCompanyMonthlyProfitAndLoss?.getCategory({ name, type: "otherIncomes" })}
                      onChange={(category) => handleCategoryOnChange({ name, type: "otherIncomes" }, { category })}
                    />
                  </div>
                  {currentCompanyMonthlyProfitAndLoss?.getItems("otherIncomes", name)?.map((fixedAsset) => (
                    <div className="flex items-center" style={{ minWidth: "10em" }}>
                      <label>{fixedAsset?.formattedValue}</label>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="mb-5">
            <div className="flex items-center">
              <div className="absolute left-0 w-full h-8 bg-gradient-to-r from-transparent bg-neutral-100 z-10 pointer-events-none"></div>
              <label className="z-20 font-semibold">Other Expenses</label>
            </div>
            <div className="mt-5">
              {currentCompanyMonthlyProfitAndLoss?.getNames("otherExpenses")?.map((name, incomeIndex) => (
                <div key={incomeIndex} className="flex items-center mb-2">
                  <div className="whitespace-pre-wrap flex items-center" style={{ minWidth: "27em" }}>
                    <label>{name.split(":").length === 3 ? [name.split(":")[0], name.split(":")[2]].join(":") : name}</label>
                  </div>
                  <button style={{ minWidth: "3em" }} onClick={() => handleOnClickGPT({ name, type: "otherExpenses" })}>🤖</button>
                  <div className="flex items-center" style={{ minWidth: "20em" }}>
                    <BVASelect
                      items={ProfitAndLoss.getCategoriesOptions("otherExpenses")}
                      itemLabel="name"
                      width="14em"
                      placeholder="Select a category"
                      value={currentCompanyMonthlyProfitAndLoss?.getCategory({ name, type: "otherExpenses" })}
                      onChange={(category) => handleCategoryOnChange({ name, type: "otherExpenses" }, { category })}
                    />
                  </div>
                  {currentCompanyMonthlyProfitAndLoss?.getItems("otherExpenses", name)?.map((fixedAsset) => (
                    <div className="flex items-center" style={{ minWidth: "10em" }}>
                      <label>{fixedAsset?.formattedValue}</label>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="py-10">
        <Divider />
      </div>
      <div className="overflow-auto">
        <ProfitAndLossRatiosConsolidation />
      </div>
      <div className="py-10">
        <Divider />
      </div>
      <div className="overflow-auto">
        <ProfitAndLossEbitda />
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  companyData: state.appState.companyData,
  dataSettings: state.appState.dataSettings,
  currentCompanyMonthlyBalanceSheet:
    state.financials.currentCompanyMonthlyBalanceSheet,
  currentCompanyMonthlyProfitAndLoss:
    state.financials.currentCompanyMonthlyProfitAndLoss,
});

const mapDispatchToProps = (dispatch) => ({
  async dispatchFetchMonthlyProfitAndLoss(
    { companyId },
    { periodsToCompare, endMonth },
    { dateOrder }
  ) {
    await dispatch(
      fetchCompanyMonthlyProfitAndLoss(
        { companyId },
        { periodsToCompare, endMonth },
        { dateOrder }
      )
    );
  },

  dispatchUpdateMonthlyProfitAndLoss({ name, type }, { category }) {
    dispatch(updateMonthlyProfitAndLoss({ name, type }, { category }));
  },

  dispatchUpdateCategory(name, category) {
    dispatch(updateCategory(name, category))
  }
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MonthlyProfitAndLossDashboard);
